import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import { API_URL } from "../../../helpers/constants";
import { cn } from "../../../helpers/utils";
import Auth from "../../../service/Auth";
import { useAuth } from "../../../providers/AuthContext";
import TransferService from "../../../service/Transfer";
import SelectDropDown from "../../../components/ui/SelectDropDown";

const TransferForm = () => {
  const { t } = useTranslation();
  const { user, updateUser } = useAuth();
  const [transferType, setTransferType] = useState("deposit");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [message, setMessage] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const authServ = new Auth(API_URL);
  const transferServ = new TransferService(API_URL);

  // Fetch users based on role hierarchy
  const fetchUsers = async (inputValue, callback) => {
    if (inputValue.length < 3) {
      callback([]);
      return;
    }
    try {
      let response;
      let options = [];
      if (user.role === "Owner") {
        response = await authServ.getAllUsers();
        options = response.users
          ?.filter((fi) => fi.username !== user.username && fi.username.toLowerCase().includes(inputValue.toLowerCase()))
          ?.map((item) => ({
            value: item._id,
            label: `${item.username} -> ${item?.role} -> ${item?.balance?.toFixed(2)}`,
          }));
      } else {
        response = await authServ.getAllCreaterUsersById(user._id);
        options = response.users
          ?.filter((fi) => fi.username !== user.username && fi.username.toLowerCase().includes(inputValue.toLowerCase()))
          ?.map((item) => ({
            value: item._id,
            label: `${item.username} -> ${item?.role} -> ${item?.balance?.toFixed(2)}`,
          }));
      }
      callback(options);
    } catch (error) {
      console.error("Error fetching users:", error);
      callback([]);

      // Handle token expiration or invalid token (401)
      if (error.response?.status === 401) {
        toast.error("Authorization failed. Please log in again.");
      } else {
        toast.error("Error fetching users. Please try again later.");
      }
    }
  };

  // Search functionality

  const handleInputChange = (value) => {
    setSearchTerm(value);
    if (value.length >= 3) {
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };
  const handleMenuClose = () => {
    setShowSuggestions(false);
  };
  const handleTransfer = async () => {
    if (!selectedUser || amount <= 0) {
      setMessage("Please select a valid user and amount.");
      return;
    }

    const transactionId = uuidv4();

    const transferData = {
      transaction_id: transactionId,
      senderId: user._id,
      receiverId: selectedUser?.value,
      amount,
      type: transferType,
      note,
    };
    try {
      let senderId = "";
      let receiverId = "";
      if (transferData.type === "withdraw") {
        receiverId = transferData.senderId;
        senderId = transferData.receiverId;
      } else {
        receiverId = transferData.receiverId;
        senderId = transferData.senderId;
      }
      const result = await transferServ.makeTransfer(
        senderId,
        receiverId,
        transferData.amount,
        transferData.type,
        transferData.note,
        transferData.transaction_id
      );

      if (result.success) {
        const updatedUserResponse = await authServ.getBalance(user.username);
        if (updatedUserResponse.success) {
          updateUser({ ...user, balance: updatedUserResponse.balance });
          toast.success("Transfer successful!");
          resetForm();
        }
      } else {
        toast.error(result.message || "Transfer failed.");
      }
    } catch (error) {
      console.error("Error during transfer:", error);
      toast.error("An error occurred during transfer.");
      // setMessage("An error occurred during transfer.");
    }
  };

  const handleIncrement = (value) => {
    setAmount((prev) => {
      const numericPrev = prev === "" ? 0 : Number(prev); // Treat empty as 0
      return numericPrev + value;
    });
  };

  const handleAmountInputChange = (e) => {
    const newValue = e.target.value;
    setAmount(newValue === "" ? "" : Number(newValue)); // Allow empty or a number
  };

  const resetForm = () => {
    setTimeout(() => {
      setMessage("");
    }, 5000);
    setAmount(0);
    setNote("");
    setSelectedUser("");
    setSearchTerm("");
    setShowSuggestions(false);
  };

  return (
    <div className="flex flex-col">
      <header className="rounded-lg sm:text-start text-center bg-[#474747] text-white w-full p-3">
        <h1 className="md:text-2xl text-lg font-bold md:text-left text-center">{t("Transfer")}</h1>
      </header>

      <div className="flex-1 pt-4">
        <div className="max-w-lg rounded-lg">
          {/* User Selection Input */}
          <div className="relative mb-5">
            {/* <span className="font-normal text-[#242424] ml-2">Choose User</span> */}
            <SelectDropDown
              label="Choose User"
              placeholder={"Type 3+ letters"}
              selectedOption={selectedUser ? selectedUser : []}
              loadOptions={fetchUsers}
              handleChange={(option) => setSelectedUser(option)}
              inputValue={searchTerm || ""}
              onInputChange={(value) => handleInputChange(value)}
              menuIsOpen={showSuggestions || false}
              onMenuClose={() => handleMenuClose()}
              isSearchable={true}
            />
          </div>

          {/* Transfer Type Section */}
          <div className="mb-4">
            <div className="grid grid-cols-2 overflow-hidden">
              {[
                { label: "Deposit", value: "deposit" },
                { label: "Withdraw", value: "withdraw" },
              ].map((option, index) => (
                <div
                  key={option.value}
                  onClick={() => setTransferType(option.value)}
                  className={`flex items-center p-3 cursor-pointer transition transform ${
                    transferType === option.value
                      ? "bg-primary text-sm font-semibold text-black shadow-md"
                      : "bg-[#e2e2e2] text-sm text-black font-semibold"
                  } ${index === 0 ? "rounded-tl-lg rounded-bl-lg" : "rounded-tr-lg rounded-br-lg"}`}
                >
                  <input
                    type="radio"
                    name="transferType"
                    value={option.value}
                    checked={transferType === option.value}
                    onChange={(e) => {
                      e.stopPropagation();
                      setTransferType(option.value);
                    }}
                    className="mr-2"
                  />
                  {option.label}
                </div>
              ))}
            </div>
          </div>

          {/* Transfer Amount Section */}
          <div className="mb-4">
            <label className="block ml-2 text-[#242424]">{t("Transfer Amount")}</label>
            <div className="telative flex space-x-4 mb-2">
              <input
                type="number"
                value={amount}
                onChange={(e) => handleAmountInputChange(e)}
                onBlur={(e) => {
                  if (e.target.value < 1 || e.target.value === 0) {
                    handleAmountInputChange({ target: { value: "1" } });
                  }
                }}
                className="text-left w-full p-2 border border-slate-800 rounded text-black"
              />
              <span className="absolute"></span>
              <button onClick={() => setAmount("")} className="border border-black py-2 px-4 rounded focus:outline-none text-black">
                {t("Clear")}
              </button>
            </div>

            <div className="flex space-x-4">
              {[10, 20, 50, 100, 500].map((value) => (
                <button key={value} onClick={() => handleIncrement(value)} className="flex-1 p-2 bg-[#e2e2e2] rounded-lg text-black font-medium">
                  +{value}
                </button>
              ))}
            </div>
          </div>

          {/* Transfer Note Section */}
          <div className="mb-4">
            <label className="block ml-2 text-[#242424]">{t("Transfer Note")}</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full p-2 border border-slate-800 rounded focus:outline-none focus:ring-2 focus:ring-primary-ybg-primary text-black"
              rows="4"
              placeholder="Add a note (optional)"
            />
          </div>

          <button
            onClick={handleTransfer}
            className="w-full font-semibold bg-primary text-black px-5 py-3 rounded-lg hover:bg-primary/80 transition duration-300"
          >
            {t("TRANSFER")}
          </button>
          <button
            onClick={resetForm}
            className="w-full mt-3 font-semibold border border-slate-800 bg-white text-black px-5 py-3 rounded-lg hover:bg-[#e2e2e2] transition duration-300"
          >
            {t("RESET")}
          </button>

          {message && <div className={cn("mt-4 p-2 text-center", message.includes("successful") ? "text-green-600" : "text-red-600")}>{message}</div>}
        </div>
      </div>
    </div>
  );
};

export default TransferForm;
